import React, { useCallback, useEffect, useState } from 'react';
import { t } from '@citrite/translate';
import { notifyError, notifyInfo } from '@citrite/web-ui-component';
import { GenericErrorBoundary } from 'App/GenericErrorBoundary';
import {
	defaultContext,
	IServiceContinuityContext,
	ServiceContinuityContext,
} from 'App/ServiceContinuity/Context';
import { LeaseFile, LeaseFileType } from 'App/ServiceContinuity/types';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { isNativeClient } from 'Environment/launchResource/device';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';

export let onLeaseSyncCompletedEvent = async (_syncStatus: string) => {};
const warningThreshold = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const _ServiceContinuityProvider: React.FC = ({ children }) => {
	const [serviceContinuityContext, setServiceContinuityContext] =
		useState<IServiceContinuityContext>(defaultContext);

	const { value: resourceContext } = useLoadableResourceContext();

	const updateContext = useCallback(
		(partialContext: Partial<IServiceContinuityContext>) => {
			setServiceContinuityContext(prevContext => ({
				...prevContext,
				...partialContext,
			}));
		},
		[]
	);

	const initializeLeaseSync = useCallback(async () => {
		const leaseSyncResult = await environment.initLeaseSync();
		if (leaseSyncResult === 'Success') {
			updateContext({
				lastClientResyncTriggerTime: new Date(),
			});
		}
		return leaseSyncResult;
	}, [updateContext]);

	useEffect(() => {
		updateContext({
			initializeLeaseSync,
			initializeLogCollection:
				environment.supportsInitLogCollection && environment.initLogCollection,
		});
	}, [initializeLeaseSync, updateContext]);

	useEffect(() => {
		const expiredLeases: Resource[] = [];
		const expiringLeases: Resource[] = [];
		const activeLeases: Resource[] = [];
		const missingLeases: Resource[] = [];
		const leaseResourceFiles: LeaseFile[] = serviceContinuityContext?.leaseFiles.filter(
			({ document }) => document.manifest.leaseType === LeaseFileType.XD_Resource
		);
		resourceContext?.resources?.forEach(resource => {
			if (!!resource?.clmetadata) {
				const leaseFile = leaseResourceFiles.find(
					({ document }) =>
						resource?.clmetadata?.resourcekey === document.manifest.resourceKey
				);
				if (leaseFile) {
					const leaseValidTo = new Date(leaseFile?.document.manifest.validTo).getTime();
					const currentDate = new Date().getTime();
					if (leaseValidTo < currentDate) {
						expiredLeases.push(resource);
					} else if (leaseValidTo - currentDate <= warningThreshold) {
						expiringLeases.push(resource);
					} else {
						activeLeases.push(resource);
					}
				} else {
					missingLeases.push(resource);
				}
			}
		});

		updateContext({
			shieldStatus: {
				expiredLeases,
				expiringLeases,
				activeLeases,
				lastCLSyncStatus: serviceContinuityContext?.shieldInformation.lastCLSyncStatus,
				lastLeaseSyncSuccessTime:
					serviceContinuityContext?.shieldInformation.lastLeaseSyncSuccessTime,
			},
		});
	}, [
		updateContext,
		resourceContext?.resources,
		serviceContinuityContext?.shieldInformation,
		serviceContinuityContext?.leaseFiles,
	]);

	const syncPlatformLeaseInformation = useCallback(async () => {
		updateContext({ isLoading: true });
		const shieldInformation = await environment.getShieldInformation();
		const leaseFiles: LeaseFile[] = [];
		if (shieldInformation?.leaseFiles) {
			for (const filePath of shieldInformation.leaseFiles) {
				const leaseFile = await environment.getLocalFile<LeaseFile>(filePath);
				leaseFiles.push(leaseFile);
			}
		}
		updateContext({ shieldInformation, leaseFiles, isLoading: false });
	}, [updateContext]);

	useEffect(() => {
		syncPlatformLeaseInformation();
	}, [syncPlatformLeaseInformation]);

	useEffect(() => {
		updateContext({ syncPlatformLeaseInformation });
	}, [syncPlatformLeaseInformation, updateContext]);

	const onLeaseSyncCompleted = useCallback(
		async (syncStatus: string) => {
			if (!serviceContinuityContext.lastClientResyncTriggerTime) {
				return;
			}
			if (syncStatus === 'Success') {
				await syncPlatformLeaseInformation();
				!!serviceContinuityContext.lastClientResyncTriggerTime &&
					notifyInfo(t('Workspace:service_continuity.sync_success'));
				updateContext({ lastClientResyncTriggerTime: null });
			} else {
				notifyError(t('Workspace:service_continuity.sync_failed'));
			}
		},
		[
			updateContext,
			syncPlatformLeaseInformation,
			serviceContinuityContext.lastClientResyncTriggerTime,
		]
	);

	useEffect(() => {
		onLeaseSyncCompletedEvent = onLeaseSyncCompleted;
	}, [onLeaseSyncCompleted]);

	return (
		<ServiceContinuityContext.Provider value={serviceContinuityContext}>
			{children}
		</ServiceContinuityContext.Provider>
	);
};

const _ServiceContinuityProviderWrapper: React.FC = props => {
	const { featureCanaryConfiguration } = useConfigurationContext().configurationService;

	const isServiceContinuityEnabled = featureCanaryConfiguration.isFeatureEnabled(
		FeatureFlag.EnableServiceContinuitySupportability
	);
	const platformSupportsServiceContinuity =
		isNativeClient() && environment.shouldEnableServiceContinuitySupportability();

	return isServiceContinuityEnabled && platformSupportsServiceContinuity ? (
		<_ServiceContinuityProvider {...props} />
	) : (
		<>{props.children}</>
	);
};

export const ServiceContinuityProvider: React.FC = props => (
	<GenericErrorBoundary
		renderProp={() => <_ServiceContinuityProviderWrapper {...props} />}
		fallback={() => <>{props.children}</>}
		name="ServiceContinuityProvider"
	/>
);
